import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { getEnvVar } from '../../../config/env';

interface WorkspaceOverride {
  id?: string;
  workspace_id?: string;
  pricing_tier_id?: string;
  whatsapp_cost_override?: number;
  voice_cost_override?: number;
  ai_task_cost_override?: number;
  email_cost_override?: number;
  sms_cost_override?: number;
  bulk_discount_threshold_override?: number;
  bulk_discount_percentage_override?: number;
  auto_topup_enabled?: boolean;
  monthly_credit_limit?: number;
  is_active?: boolean;
}

interface PricingTier {
  id: string;
  tier_name: string;
  tier_description?: string;
}

interface WorkspacePricingModalProps {
  override: WorkspaceOverride | null;
  onClose: () => void;
  onSave: () => void;
}

export const WorkspacePricingModal: React.FC<WorkspacePricingModalProps> = ({ override, onClose, onSave }) => {
  const [formData, setFormData] = useState<WorkspaceOverride>({
    workspace_id: '',
    pricing_tier_id: '',
    whatsapp_cost_override: undefined,
    voice_cost_override: undefined,
    ai_task_cost_override: undefined,
    email_cost_override: undefined,
    sms_cost_override: undefined,
    bulk_discount_threshold_override: undefined,
    bulk_discount_percentage_override: undefined,
    auto_topup_enabled: false,
    monthly_credit_limit: undefined,
    is_active: true,
  });
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

  useEffect(() => {
    loadWorkspaces();
    loadTiers();
    if (override) {
      setFormData(override);
    }
  }, [override]);

  const loadWorkspaces = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/workspaces`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setWorkspaces(result.data || []);
        }
      }
    } catch (err) {
      console.error('Error loading workspaces:', err);
    }
  };

  const loadTiers = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/pricing/tiers?active_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTiers(result.data || []);
        }
      }
    } catch (err) {
      console.error('Error loading tiers:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('admin_auth_token');
      
      // Prepare data - only include fields that have values
      const payload: any = {
        workspace_id: formData.workspace_id,
        is_active: formData.is_active ?? true,
      };

      if (formData.pricing_tier_id) payload.pricing_tier_id = formData.pricing_tier_id;
      if (formData.whatsapp_cost_override !== undefined) payload.whatsapp_cost_override = formData.whatsapp_cost_override;
      if (formData.voice_cost_override !== undefined) payload.voice_cost_override = formData.voice_cost_override;
      if (formData.ai_task_cost_override !== undefined) payload.ai_task_cost_override = formData.ai_task_cost_override;
      if (formData.email_cost_override !== undefined) payload.email_cost_override = formData.email_cost_override;
      if (formData.sms_cost_override !== undefined) payload.sms_cost_override = formData.sms_cost_override;
      if (formData.bulk_discount_threshold_override !== undefined) payload.bulk_discount_threshold_override = formData.bulk_discount_threshold_override;
      if (formData.bulk_discount_percentage_override !== undefined) payload.bulk_discount_percentage_override = formData.bulk_discount_percentage_override;
      if (formData.auto_topup_enabled !== undefined) payload.auto_topup_enabled = formData.auto_topup_enabled;
      if (formData.monthly_credit_limit !== undefined) payload.monthly_credit_limit = formData.monthly_credit_limit;

      const url = override?.workspace_id
        ? `${API_BASE_URL}/api/v1/admin/pricing/workspaces/${override.workspace_id}`
        : `${API_BASE_URL}/api/v1/admin/pricing/workspaces`;
      
      const method = override?.workspace_id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save workspace pricing override');
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save workspace pricing override');
      console.error('Error saving workspace pricing override:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof WorkspaceOverride, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {override ? 'Edit Workspace Pricing' : 'Add Workspace Pricing Override'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="space-y-6">
              {/* Workspace Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workspace *
                </label>
                <select
                  required
                  value={formData.workspace_id || ''}
                  onChange={(e) => handleChange('workspace_id', e.target.value)}
                  disabled={!!override?.workspace_id}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Select workspace...</option>
                  {workspaces.map((ws) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Pricing Tier */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pricing Tier (Optional - uses default if not set)
                </label>
                <select
                  value={formData.pricing_tier_id || ''}
                  onChange={(e) => handleChange('pricing_tier_id', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Use default tier</option>
                  {tiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.tier_name} {tier.tier_description && `- ${tier.tier_description}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cost Overrides */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Cost Overrides (Optional - leave blank to use tier defaults)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WhatsApp (per message)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.whatsapp_cost_override ?? ''}
                      onChange={(e) => handleChange('whatsapp_cost_override', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Override tier default"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voice (per minute)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.voice_cost_override ?? ''}
                      onChange={(e) => handleChange('voice_cost_override', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Override tier default"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      AI Task (per execution)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.ai_task_cost_override ?? ''}
                      onChange={(e) => handleChange('ai_task_cost_override', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Override tier default"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email (per send)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.email_cost_override ?? ''}
                      onChange={(e) => handleChange('email_cost_override', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Override tier default"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SMS (per message)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.sms_cost_override ?? ''}
                      onChange={(e) => handleChange('sms_cost_override', e.target.value ? parseFloat(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Override tier default"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-4">Additional Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Credit Limit (Optional)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.monthly_credit_limit ?? ''}
                      onChange={(e) => handleChange('monthly_credit_limit', e.target.value ? parseInt(e.target.value) : undefined)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="No limit"
                    />
                  </div>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.auto_topup_enabled ?? false}
                      onChange={(e) => handleChange('auto_topup_enabled', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Auto Top-up Enabled</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active ?? true}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : override ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
